/**
 * Topical taxonomy. Two-level: top-level domains + optional sub-domains.
 *
 * IDs are stable snake_case and become foreign keys on Card.topic / Deck.topic.
 * `name` and `hint` are localized; sub-topics live inside their parent's
 * `children` array. Use `TOPICS_FLAT` for one-pass scans (forms, filters).
 */

import type { Localized } from "./locale";

export type TopicId =
  | "basic"
  | "family"
  | "emotions"
  | "daily_life"
  | "travel"
  | "food"
  | "nature"
  | "city"
  | "education"
  | "business"
  | "management"
  | "finance"
  | "marketing"
  | "law"
  | "medicine"
  | "anatomy"
  | "psychology"
  | "engineering"
  | "it"
  | "ai"
  | "science"
  | "physics"
  | "chemistry"
  | "biology"
  | "mathematics"
  | "astronomy"
  | "philosophy"
  | "history"
  | "art"
  | "music"
  | "sport"
  | "military"
  | "culture"
  | "religion";

export type TopicRef = {
  id: TopicId;
  name: Localized;
  hint?: Localized;
  icon?: string;
  children?: TopicRef[];
};

export const TOPICS: TopicRef[] = [
  {
    id: "basic",
    name: { ru: "Базовая речь", en: "Basics", es: "Básico", fr: "Bases", de: "Grundlagen", it: "Base", zh: "基础", ja: "基本" },
    hint: { ru: "Самые частые слова и фразы", en: "Most common words and phrases", es: "Las palabras y frases más frecuentes", fr: "Mots et phrases les plus courants", de: "Häufigste Wörter und Sätze", it: "Parole e frasi più comuni", zh: "最常见的单词和短语", ja: "もっとも頻出する単語とフレーズ" },
    icon: "Sparkles",
    children: [
      { id: "family",     name: { ru: "Семья",         en: "Family",      es: "Familia",      fr: "Famille",   de: "Familie",     it: "Famiglia",      zh: "家庭",     ja: "家族" } },
      { id: "emotions",   name: { ru: "Эмоции",        en: "Emotions",    es: "Emociones",    fr: "Émotions",  de: "Emotionen",   it: "Emozioni",      zh: "情感",     ja: "感情" } },
      { id: "daily_life", name: { ru: "Быт",           en: "Daily life",  es: "Vida diaria",  fr: "Quotidien", de: "Alltag",      it: "Vita quotidiana", zh: "日常生活", ja: "日常生活" } },
      { id: "food",       name: { ru: "Еда",           en: "Food",        es: "Comida",       fr: "Nourriture",de: "Essen",       it: "Cibo",          zh: "食物",     ja: "食べ物" } },
      { id: "travel",     name: { ru: "Путешествия",   en: "Travel",      es: "Viajes",       fr: "Voyages",   de: "Reisen",      it: "Viaggi",        zh: "旅行",     ja: "旅行" } },
      { id: "nature",     name: { ru: "Природа",       en: "Nature",      es: "Naturaleza",   fr: "Nature",    de: "Natur",       it: "Natura",        zh: "自然",     ja: "自然" } },
      { id: "city",       name: { ru: "Город",         en: "City",        es: "Ciudad",       fr: "Ville",     de: "Stadt",       it: "Città",         zh: "城市",     ja: "街・都市" } },
    ],
  },
  {
    id: "education",
    name: { ru: "Образование", en: "Education", es: "Educación", fr: "Éducation", de: "Bildung", it: "Istruzione", zh: "教育", ja: "教育" },
    icon: "GraduationCap",
    children: [
      { id: "history",    name: { ru: "История",      en: "History",    es: "Historia",   fr: "Histoire",   de: "Geschichte", it: "Storia",     zh: "历史",   ja: "歴史" } },
      { id: "philosophy", name: { ru: "Философия",    en: "Philosophy", es: "Filosofía",  fr: "Philosophie",de: "Philosophie",it: "Filosofia",  zh: "哲学",   ja: "哲学" } },
      { id: "art",        name: { ru: "Искусство",    en: "Art",        es: "Arte",       fr: "Art",        de: "Kunst",      it: "Arte",       zh: "艺术",   ja: "芸術" } },
      { id: "music",      name: { ru: "Музыка",       en: "Music",      es: "Música",     fr: "Musique",    de: "Musik",      it: "Musica",     zh: "音乐",   ja: "音楽" } },
    ],
  },
  {
    id: "business",
    name: { ru: "Бизнес", en: "Business", es: "Negocios", fr: "Affaires", de: "Geschäft", it: "Affari", zh: "商业", ja: "ビジネス" },
    icon: "Briefcase",
    children: [
      { id: "management", name: { ru: "Менеджмент", en: "Management", es: "Gestión",   fr: "Gestion",    de: "Management", it: "Gestione", zh: "管理",   ja: "経営" } },
      { id: "finance",    name: { ru: "Финансы",    en: "Finance",    es: "Finanzas",  fr: "Finance",    de: "Finanzen",   it: "Finanza",  zh: "金融",   ja: "金融" } },
      { id: "marketing",  name: { ru: "Маркетинг",  en: "Marketing",  es: "Marketing", fr: "Marketing",  de: "Marketing",  it: "Marketing",zh: "市场营销", ja: "マーケティング" } },
      { id: "law",        name: { ru: "Право",      en: "Law",        es: "Derecho",   fr: "Droit",      de: "Recht",      it: "Diritto",  zh: "法律",   ja: "法律" } },
    ],
  },
  {
    id: "medicine",
    name: { ru: "Медицина", en: "Medicine", es: "Medicina", fr: "Médecine", de: "Medizin", it: "Medicina", zh: "医学", ja: "医学" },
    icon: "Stethoscope",
    children: [
      { id: "anatomy",    name: { ru: "Анатомия",   en: "Anatomy",    es: "Anatomía",   fr: "Anatomie",   de: "Anatomie",   it: "Anatomia",   zh: "解剖学", ja: "解剖学" } },
      { id: "psychology", name: { ru: "Психология", en: "Psychology", es: "Psicología", fr: "Psychologie",de: "Psychologie",it: "Psicologia", zh: "心理学", ja: "心理学" } },
    ],
  },
  {
    id: "science",
    name: { ru: "Наука", en: "Science", es: "Ciencia", fr: "Science", de: "Wissenschaft", it: "Scienza", zh: "科学", ja: "科学" },
    icon: "FlaskConical",
    children: [
      { id: "mathematics", name: { ru: "Математика", en: "Mathematics",es: "Matemáticas",fr: "Mathématiques",de: "Mathematik",it: "Matematica",zh: "数学",     ja: "数学" } },
      { id: "physics",     name: { ru: "Физика",     en: "Physics",    es: "Física",     fr: "Physique",     de: "Physik",     it: "Fisica",    zh: "物理",     ja: "物理学" } },
      { id: "chemistry",   name: { ru: "Химия",      en: "Chemistry",  es: "Química",    fr: "Chimie",       de: "Chemie",     it: "Chimica",   zh: "化学",     ja: "化学" } },
      { id: "biology",     name: { ru: "Биология",   en: "Biology",    es: "Biología",   fr: "Biologie",     de: "Biologie",   it: "Biologia",  zh: "生物学",   ja: "生物学" } },
      { id: "astronomy",   name: { ru: "Астрономия", en: "Astronomy",  es: "Astronomía", fr: "Astronomie",   de: "Astronomie", it: "Astronomia",zh: "天文学",   ja: "天文学" } },
    ],
  },
  {
    id: "engineering",
    name: { ru: "Инженерия", en: "Engineering", es: "Ingeniería", fr: "Ingénierie", de: "Technik", it: "Ingegneria", zh: "工程", ja: "工学" },
    icon: "Wrench",
    children: [
      { id: "it", name: { ru: "IT", en: "IT", es: "Informática", fr: "Informatique", de: "IT", it: "Informatica", zh: "信息技术", ja: "IT" } },
      { id: "ai", name: { ru: "Искусственный интеллект", en: "Artificial Intelligence", es: "Inteligencia Artificial", fr: "Intelligence Artificielle", de: "Künstliche Intelligenz", it: "Intelligenza Artificiale", zh: "人工智能", ja: "人工知能" } },
    ],
  },
  {
    id: "culture",
    name: { ru: "Культура", en: "Culture", es: "Cultura", fr: "Culture", de: "Kultur", it: "Cultura", zh: "文化", ja: "文化" },
    icon: "Globe",
    children: [
      { id: "religion", name: { ru: "Религия",            en: "Religion",      es: "Religión",     fr: "Religion",    de: "Religion", it: "Religione", zh: "宗教",   ja: "宗教" } },
      { id: "sport",    name: { ru: "Спорт",              en: "Sport",         es: "Deporte",      fr: "Sport",       de: "Sport",    it: "Sport",     zh: "体育",   ja: "スポーツ" } },
      { id: "military", name: { ru: "Военная тематика",   en: "Military",      es: "Militar",      fr: "Militaire",   de: "Militär",  it: "Militare",  zh: "军事",   ja: "軍事" } },
    ],
  },
];

/** Flattened list — every top-level and child, in declaration order. */
export const TOPICS_FLAT: TopicRef[] = TOPICS.flatMap((t) => [t, ...(t.children ?? [])]);

export const TOPIC_BY_ID: Record<TopicId, TopicRef> = Object.fromEntries(
  TOPICS_FLAT.map((t) => [t.id, t]),
) as Record<TopicId, TopicRef>;

export function isTopicId(value: unknown): value is TopicId {
  return typeof value === "string" && value in TOPIC_BY_ID;
}
